import { PaymentTypes, PlanIntervals } from '@/payment/types';
import { DEFAULT_STORAGE_PROVIDER } from '@/storage/config/storage-provider';
import type { WebsiteConfig } from '@/types';
import { websitePublicConfig } from './website-public';

export function isTurnstileDevelopmentEnabled() {
  return (
    process.env.NODE_ENV === 'development' &&
    process.env.NEXT_PUBLIC_TURNSTILE_ENABLED?.trim() === 'true'
  );
}

/**
 * website config, without translations
 *
 * docs:
 * https://imgenhancer.app/docs
 */
export const websiteConfig: WebsiteConfig = {
  ui: {
    theme: {
      defaultTheme: 'default',
      enableSwitch: true,
    },
    mode: {
      defaultMode: 'dark',
      enableSwitch: false,
    },
  },
  metadata: {
    images: {
      ogImage: '/og.png',
      logoLight: '/android-chrome-192x192.png',
      logoDark: '/android-chrome-192x192.png',
    },
    social: {
      discord: 'https://discord.gg/xc7VcUsZDq',
    },
  },
  features: {
    enableUpgradeCard: true,
    enableUpdateAvatar: true,
    enableDatafastRevenueTrack: false,
    enableCrispChat: !!process.env.NEXT_PUBLIC_CRISP_WEBSITE_ID,
    enableTurnstileCaptcha:
      Boolean(process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim()) &&
      (process.env.NODE_ENV !== 'development' ||
        isTurnstileDevelopmentEnabled()),
    enableDeferredHeaderUserControls: true,
    enableDeferredContactFormMount: true,
    enableI18nSlimProvider: true,
  },
  routes: {
    defaultLoginRedirect: '/',
  },
  auth: {
    enableGoogleLogin: websitePublicConfig.auth.enableGoogleLogin,
    enableCredentialLogin: false,
  },
  i18n: websitePublicConfig.i18n,
  blog: {
    enable: false,
    paginationSize: 6,
    relatedPostsSize: 3,
  },
  changelog: {
    enable: true,
  },
  docs: {
    enable: false,
  },
  mail: {
    provider: 'resend',
    fromEmail: 'ImgEnhancer.app <hello@imgenhancer.app>',
    supportEmail: websitePublicConfig.support.email,
  },
  newsletter: {
    enable: false,
    provider: 'resend',
    autoSubscribeAfterSignUp: false,
  },
  storage: {
    enable: true,
    provider: DEFAULT_STORAGE_PROVIDER,
  },
  price: {
    plans: {
      free: {
        id: 'free',
        prices: [],
        isFree: true,
        credits: {
          enable: false,
          amount: 0,
        },
      },
      'credits-200': {
        id: 'credits-200',
        prices: [
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-200-monthly',
            amount: 990,
            currency: 'USD',
            interval: PlanIntervals.MONTH,
          },
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-200-yearly',
            amount: 9900,
            currency: 'USD',
            interval: PlanIntervals.YEAR,
          },
        ],
        isFree: false,
        popular: false,
        credits: {
          enable: true,
          amount: 200,
          expireDays: 30,
        },
      },
      'credits-400': {
        id: 'credits-400',
        prices: [
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-400-monthly',
            amount: 1690,
            currency: 'USD',
            interval: PlanIntervals.MONTH,
          },
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-400-yearly',
            amount: 16900,
            currency: 'USD',
            interval: PlanIntervals.YEAR,
          },
        ],
        isFree: false,
        popular: false,
        credits: {
          enable: true,
          amount: 400,
          expireDays: 30,
        },
      },
      'credits-800': {
        id: 'credits-800',
        prices: [
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-800-monthly',
            amount: 2990,
            currency: 'USD',
            interval: PlanIntervals.MONTH,
          },
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-800-yearly',
            amount: 29900,
            currency: 'USD',
            interval: PlanIntervals.YEAR,
          },
        ],
        isFree: false,
        popular: false,
        credits: {
          enable: true,
          amount: 800,
          expireDays: 30,
        },
      },
      'credits-2000': {
        id: 'credits-2000',
        prices: [
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-2000-monthly',
            amount: 5990,
            currency: 'USD',
            interval: PlanIntervals.MONTH,
          },
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-2000-yearly',
            amount: 59900,
            currency: 'USD',
            interval: PlanIntervals.YEAR,
          },
        ],
        isFree: false,
        popular: false,
        credits: {
          enable: true,
          amount: 2000,
          expireDays: 30,
        },
      },
      'credits-4500': {
        id: 'credits-4500',
        prices: [
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-4500-monthly',
            amount: 9990,
            currency: 'USD',
            interval: PlanIntervals.MONTH,
          },
          {
            type: PaymentTypes.SUBSCRIPTION,
            priceId: 'credits-4500-yearly',
            amount: 99900,
            currency: 'USD',
            interval: PlanIntervals.YEAR,
          },
        ],
        isFree: false,
        popular: false,
        credits: {
          enable: true,
          amount: 4500,
          expireDays: 30,
        },
      },
    },
  },
  credits: {
    enableCredits: true,
    enablePackagesForFreePlan: false,
    registerGiftCredits: {
      enable: true,
    },
    packages: {
      'credits-100': {
        id: 'credits-100',
        popular: false,
        amount: 100,
        price: {
          priceId: 'credits-pack-100',
          amount: 990,
          currency: 'USD',
        },
      },
      'credits-200': {
        id: 'credits-200',
        popular: false,
        amount: 200,
        price: {
          priceId: 'credits-pack-200',
          amount: 1690,
          currency: 'USD',
        },
      },
      'credits-400': {
        id: 'credits-400',
        popular: false,
        amount: 400,
        price: {
          priceId: 'credits-pack-400',
          amount: 2990,
          currency: 'USD',
        },
      },
      'credits-1200-usd-5990': {
        id: 'credits-1200-usd-5990',
        popular: false,
        amount: 1200,
        price: {
          priceId: 'credits-pack-1200-usd-5990',
          amount: 5990,
          currency: 'USD',
        },
      },
      'credits-3000-usd-9990': {
        id: 'credits-3000-usd-9990',
        popular: false,
        amount: 3000,
        price: {
          priceId: 'credits-pack-3000-usd-9990',
          amount: 9990,
          currency: 'USD',
        },
      },
      'credits-30-usd-990': {
        id: 'credits-30-usd-990',
        popular: false,
        amount: 30,
        disabled: true,
        price: {
          priceId: 'credits-pack-30-usd-990',
          amount: 990,
          currency: 'USD',
        },
      },
      'credits-100-usd-1690': {
        id: 'credits-100-usd-1690',
        popular: false,
        amount: 100,
        disabled: true,
        price: {
          priceId: 'credits-pack-100-usd-1690',
          amount: 1690,
          currency: 'USD',
        },
      },
      'credits-1000': {
        id: 'credits-1000',
        popular: false,
        amount: 1000,
        disabled: true,
        price: {
          priceId: 'credits-pack-1000',
          amount: 5990,
          currency: 'USD',
        },
      },
      'credits-2000': {
        id: 'credits-2000',
        popular: false,
        amount: 2000,
        disabled: true,
        price: {
          priceId: 'credits-pack-2000',
          amount: 9990,
          currency: 'USD',
        },
      },
    },
  },
};
